import { Button, Card, Center, Spinner, Stack, Text, useToast } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { modelUtils, permissionHierarchy, PermissionLevel } from '@pelicargo/utils';
import { ShipmentFormPayment } from '@prisma/client';
import { useMemo } from 'react';

import { TrpcOutputs } from '../../../config/trpc';
import { useAuth } from '../../../context/AuthProvider';
import { GetRequestOutput } from '../../../hooks/request/useRequest';
import { useShipmentFormMutations } from '../../../hooks/shipmentForm/useShipmentFormMutations';
import { useShipmentForms } from '../../../hooks/shipmentForm/useShipmentForms';
import { Quote } from '../../../utils/types';
import { AwbInstructions } from './AwbInstructions';

type Cargo = TrpcOutputs['getCargosByRequestId'][number];

type Props = {
  request?: GetRequestOutput;
  bookedQuote?: Quote;
  cargoList?: Cargo[];
  hasAesFilingInBookedInlandQuote?: boolean;
  inlandQuoteVariantIdForAes?: number;
};

export const PaperworkSection = ({
  request,
  bookedQuote,
  cargoList,
  hasAesFilingInBookedInlandQuote,
  inlandQuoteVariantIdForAes,
}: Props) => {
  const toast = useToast();
  const { permission } = useAuth();
  const requestId = request?.id;
  const quoteId = bookedQuote?.id;
  const canManagePaperwork =
    !!permission && permissionHierarchy[permission] >= permissionHierarchy[PermissionLevel.CSMEMBER];

  // No explicit refetch: every mutation in useShipmentFormMutations invalidates this
  // query, and useShipmentForms also keeps the list live over its subscriptions.
  const { data: forms, isLoading } = useShipmentForms(requestId, quoteId, { enabled: canManagePaperwork });
  const { createShipmentForm, isCreating } = useShipmentFormMutations();

  const [mawb, ...hawbs] = useMemo(() => modelUtils.shipmentForms.orderMawbFirst(forms), [forms]);

  const handleCreateMawb = async () => {
    if (!requestId || !quoteId) return;
    try {
      await createShipmentForm({
        request_id: requestId,
        quote_id: quoteId,
        payment: ShipmentFormPayment.PRE_PAID,
      });
    } catch (error: any) {
      toast({ title: 'Could not create paperwork', description: error?.message, status: 'error' });
    }
  };

  return (
    <Card p={4}>
      <Stack spacing={4}>
        <Text variant="heading">Paperwork</Text>

        <Show if={!bookedQuote}>
          <Text fontSize="sm" color="fg.muted">
            No booked quote to attach paperwork to.
          </Text>
        </Show>

        <Show if={!!bookedQuote && !canManagePaperwork}>
          <Text fontSize="sm" color="fg.muted">
            Paperwork is available to customer service admins and above.
          </Text>
        </Show>

        <Show if={!!bookedQuote && canManagePaperwork && isLoading}>
          <Center py={6}>
            <Spinner />
          </Center>
        </Show>

        <Show if={!!bookedQuote && canManagePaperwork && !isLoading && !mawb}>
          <Stack spacing={3} align="flex-start">
            <Text fontSize="sm" color="fg.muted">
              No AWB instructions yet.
            </Text>
            <Button size="sm" colorScheme="primary" onClick={handleCreateMawb} isLoading={isCreating}>
              Create paperwork
            </Button>
          </Stack>
        </Show>

        <Show if={!!bookedQuote && canManagePaperwork && !isLoading && !!mawb}>
          {mawb && quoteId && requestId && (
            <AwbInstructions
              mawb={mawb}
              hawbs={hawbs}
              requestId={requestId}
              accountId={request?.account_id}
              quoteId={quoteId}
              cargoList={cargoList}
              hasAesFilingInBookedInlandQuote={hasAesFilingInBookedInlandQuote}
              inlandQuoteVariantIdForAes={inlandQuoteVariantIdForAes}
            />
          )}
        </Show>
      </Stack>
    </Card>
  );
};
